import {
  Menu as BaseMenu,
  type MenuContentProps,
  type MenuItemProps,
  type MenuRootProps as MenuProps,
  type MenuRootProps,
  type MenuSeparatorProps,
  type MenuTriggerItemProps,
  Portal,
} from "@ark-ui/react";
import clsx from "clsx";
import * as React from "react";
import { useThemeContext } from "../theme/Theme.js";

type MenuPositioningProps = Pick<MenuRootProps, "positioning">;

const Menu = (props: MenuRootProps) => {
  const { lazyMount = true, unmountOnExit = true, ...rest } = props;
  return (
    <BaseMenu.Root
      lazyMount={lazyMount}
      unmountOnExit={unmountOnExit}
      {...rest}
    />
  );
};

const MenuTrigger = BaseMenu.Trigger;
const MenuContext = BaseMenu.Context;

const MenuContent = React.forwardRef<
  React.ElementRef<typeof BaseMenu.Content>,
  MenuContentProps
>(({ className, ...props }, ref) => {
  const { primaryColor, secondaryColor } = useThemeContext();
  return (
    <BaseMenu.Positioner
      data-primary-color={primaryColor}
      data-secondary-color={secondaryColor}
    >
      <BaseMenu.Content
        ref={ref}
        className={clsx("ox-reset ox-MenuContent", className)}
        {...props}
      />
    </BaseMenu.Positioner>
  );
});

const MenuItem = React.forwardRef<
  React.ElementRef<typeof BaseMenu.Item>,
  MenuItemProps
>(({ className, ...props }, ref) => {
  return (
    <BaseMenu.Item
      ref={ref}
      className={clsx("ox-reset ox-MenuItem", className)}
      {...props}
    />
  );
});

const MenuSeparator = React.forwardRef<
  React.ElementRef<typeof BaseMenu.Separator>,
  MenuSeparatorProps
>(({ className, ...props }, ref) => {
  return (
    <BaseMenu.Separator
      ref={ref}
      className={clsx("ox-reset ox-MenuSeparator", className)}
      {...props}
    />
  );
});

const MenuSub = (props: MenuRootProps) => {
  const defaultPositioning: MenuPositioningProps["positioning"] = {
    placement: "right-start",
    gutter: -2,
  };

  const positioning = {
    ...defaultPositioning,
    ...props.positioning,
  };

  return <BaseMenu.Root positioning={positioning} {...props} />;
};

const MenuSubTrigger = React.forwardRef<
  React.ElementRef<typeof BaseMenu.TriggerItem>,
  MenuTriggerItemProps
>(({ className, ...props }, ref) => {
  return (
    <BaseMenu.TriggerItem
      ref={ref}
      className={clsx("ox-reset ox-MenuItem", className)}
      {...props}
    />
  );
});

const MenuSubContent = React.forwardRef<
  React.ElementRef<typeof BaseMenu.Content>,
  MenuContentProps
>(({ className, ...props }, ref) => {
  const { primaryColor, secondaryColor } = useThemeContext();
  return (
    <Portal>
      <BaseMenu.Positioner
        data-primary-color={primaryColor}
        data-secondary-color={secondaryColor}
      >
        <BaseMenu.Content
          ref={ref}
          className={clsx("ox-reset ox-MenuContent", className)}
          {...props}
        />
      </BaseMenu.Positioner>
    </Portal>
  );
});
(Menu as React.FC).displayName = "Menu";
MenuTrigger.displayName = "MenuTrigger";
MenuContent.displayName = "MenuContent";
MenuItem.displayName = "MenuItem";
MenuSeparator.displayName = "MenuSeparator";
MenuSubTrigger.displayName = "MenuSubTrigger";
MenuSub.displayName = "MenuSub";
MenuSubContent.displayName = "MenuSubContent";

export {
  Menu,
  MenuTrigger,
  MenuContent,
  MenuItem,
  MenuSeparator,
  MenuSubTrigger,
  MenuSub,
  MenuSubContent,
  MenuContext,
};
export type { MenuProps };
